import React, { useEffect, useMemo, useState } from "react";

import { Plus, X } from "lucide-react";

import { authApi, getApiErrorMessage } from "../api";

const black = "#000000";

const card = {
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: 18,
  boxShadow: "0 10px 24px rgba(15,23,42,.05)",
};

const input = {
  width: "100%",
  boxSizing: "border-box",
  border: "1px solid #cbd5e1",
  borderRadius: 12,
  padding: "10px 12px",
  fontSize: 13,
  background: "#ffffff",
  color: black,
};

const emptyForm = {
  provider_name: "",
  provider_code: "",
  contact_person: "",
  contact_phone: "",
  contact_email: "",
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  pincode: "",
  gstin: "",
  service_types: "STANDARD, EXPRESS",
  base_rate_per_kg: "",
  tracking_url_template: "",
  preferred_rank: 999,
  insurance_available: false,
  is_active: true,
  notes: "",
};

function ProviderModal({ open, editingId, form, setField, saving, error, success, onClose, onSubmit }) {
  const title = useMemo(() => editingId ? "Update Logistics Provider" : "Create Logistics Provider", [editingId]);
  if (!open) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        zIndex: 3000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: "min(920px, 100%)",
          maxHeight: "88vh",
          overflowY: "auto",
          background: "#ffffff",
          borderRadius: 24,
          boxShadow: "0 24px 70px rgba(15,23,42,.25)",
          padding: 24,
          color: black,
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 18 }}>
          <div>
            <div style={{ fontSize: 24, fontWeight: 900, color: black }}>{title}</div>
            <div style={{ marginTop: 4, fontSize: 13, color: black }}>
              Fill the provider details and click the create button to store the logistics partner.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ border: "1px solid #cbd5e1", background: "#ffffff", color: black, borderRadius: 12, width: 40, height: 40, cursor: "pointer" }}
            aria-label="Close provider modal"
          >
            <X size={16} />
          </button>
        </div>

        {error ? <div style={{ marginBottom: 12, color: "#b91c1c", fontWeight: 700 }}>{error}</div> : null}
        {success ? <div style={{ marginBottom: 12, color: "#166534", fontWeight: 700 }}>{success}</div> : null}

        <form onSubmit={onSubmit} style={{ display: "grid", gap: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 12 }}>
            <input style={input} placeholder="Provider name" value={form.provider_name} onChange={(e) => setField("provider_name", e.target.value)} required />
            <input style={input} placeholder="Provider code" value={form.provider_code} onChange={(e) => setField("provider_code", e.target.value.toUpperCase())} required />
            <input style={input} placeholder="Preferred rank" type="number" value={form.preferred_rank} onChange={(e) => setField("preferred_rank", e.target.value)} />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 12 }}>
            <input style={input} placeholder="Contact person" value={form.contact_person} onChange={(e) => setField("contact_person", e.target.value)} />
            <input style={input} placeholder="Contact phone" value={form.contact_phone} onChange={(e) => setField("contact_phone", e.target.value)} />
            <input style={input} placeholder="Contact email" value={form.contact_email} onChange={(e) => setField("contact_email", e.target.value)} />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 12 }}>
            <input style={input} placeholder="Address line 1" value={form.address_line1} onChange={(e) => setField("address_line1", e.target.value)} />
            <input style={input} placeholder="Address line 2" value={form.address_line2} onChange={(e) => setField("address_line2", e.target.value)} />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,minmax(0,1fr))", gap: 12 }}>
            <input style={input} placeholder="City" value={form.city} onChange={(e) => setField("city", e.target.value)} />
            <input style={input} placeholder="State" value={form.state} onChange={(e) => setField("state", e.target.value)} />
            <input style={input} placeholder="Pincode" value={form.pincode} onChange={(e) => setField("pincode", e.target.value)} />
            <input style={input} placeholder="GSTIN" value={form.gstin} onChange={(e) => setField("gstin", e.target.value)} />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 12 }}>
            <input style={input} placeholder="Service types: STANDARD, EXPRESS" value={form.service_types} onChange={(e) => setField("service_types", e.target.value)} />
            <input style={input} placeholder="Base rate per kg" type="number" step="0.01" value={form.base_rate_per_kg} onChange={(e) => setField("base_rate_per_kg", e.target.value)} />
            <input style={input} placeholder="Tracking URL template" value={form.tracking_url_template} onChange={(e) => setField("tracking_url_template", e.target.value)} />
          </div>

          <textarea
            style={{ ...input, minHeight: 84, resize: "vertical" }}
            placeholder="Internal notes"
            value={form.notes}
            onChange={(e) => setField("notes", e.target.value)}
          />

          <div style={{ display: "flex", gap: 18, alignItems: "center", flexWrap: "wrap", color: black }}>
            <label style={{ display: "flex", gap: 8, alignItems: "center", color: black, fontWeight: 600 }}>
              <input type="checkbox" checked={form.insurance_available} onChange={(e) => setField("insurance_available", e.target.checked)} />
              Insurance available
            </label>
            <label style={{ display: "flex", gap: 8, alignItems: "center", color: black, fontWeight: 600 }}>
              <input type="checkbox" checked={form.is_active} onChange={(e) => setField("is_active", e.target.checked)} />
              Active
            </label>
          </div>

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button
              type="button"
              onClick={onClose}
              style={{ border: "1px solid #cbd5e1", borderRadius: 12, background: "#ffffff", color: black, padding: "11px 16px", fontWeight: 800, cursor: "pointer" }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{ border: 0, borderRadius: 12, background: "#0f172a", color: "#ffffff", padding: "11px 16px", fontWeight: 800, cursor: "pointer" }}
            >
              {saving ? "Saving..." : editingId ? "Update Provider" : "Create Provider"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function LogisticsProviders() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [showModal, setShowModal] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await authApi.get("/logistics/providers");
      setProviders(res.data?.data || []);
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to load logistics providers."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const openCreateModal = () => {
    resetForm();
    setError("");
    setSuccess("");
    setShowModal(true);
  };

  const openEditModal = (provider) => {
    setEditingId(provider.id);
    setForm({
      provider_name: provider.provider_name || "",
      provider_code: provider.provider_code || "",
      contact_person: provider.contact_person || "",
      contact_phone: provider.contact_phone || "",
      contact_email: provider.contact_email || "",
      address_line1: provider.address_line1 || "",
      address_line2: provider.address_line2 || "",
      city: provider.city || "",
      state: provider.state || "",
      pincode: provider.pincode || "",
      gstin: provider.gstin || "",
      service_types: (provider.service_types || []).join(", "),
      base_rate_per_kg: provider.base_rate_per_kg ?? "",
      tracking_url_template: provider.tracking_url_template || "",
      preferred_rank: provider.preferred_rank ?? 999,
      insurance_available: !!provider.insurance_available,
      is_active: provider.is_active !== false,
      notes: provider.notes || "",
    });
    setError("");
    setSuccess("");
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const payload = {
        ...form,
        preferred_rank: Number(form.preferred_rank || 999),
        base_rate_per_kg: form.base_rate_per_kg === "" ? null : Number(form.base_rate_per_kg),
        service_types: form.service_types.split(",").map((item) => item.trim()).filter(Boolean),
      };
      if (editingId) {
        await authApi.put(`/logistics/providers/${editingId}`, payload);
        setSuccess("Logistics provider updated successfully.");
      } else {
        await authApi.post("/logistics/providers", payload);
        setSuccess("Logistics provider created successfully.");
      }
      await load();
      closeModal();
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to save logistics provider."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: "grid", gap: 20, color: black }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 26, fontWeight: 900, color: black }}>Logistics Providers</h2>
          <div style={{ marginTop: 6, color: black, fontSize: 14 }}>
            Manage courier partners, service options, pricing hints, and tracking templates for dispatch label generation.
          </div>
        </div>
        <button
          type="button"
          onClick={openCreateModal}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            border: 0,
            borderRadius: 12,
            background: "#0f172a",
            color: "#ffffff",
            padding: "12px 16px",
            fontWeight: 800,
            cursor: "pointer",
          }}
        >
          <Plus size={16} />
          Create Logistics Provider
        </button>
      </div>

      {error && !showModal ? <div style={{ color: "#b91c1c", fontWeight: 700 }}>{error}</div> : null}
      {success && !showModal ? <div style={{ color: "#166534", fontWeight: 700 }}>{success}</div> : null}

      <div style={{ ...card, padding: 20, color: black }}>
        <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 14, color: black }}>Configured Providers</div>
        {loading ? <div style={{ color: black }}>Loading providers...</div> : providers.length === 0 ? (
          <div style={{ color: black }}>No logistics providers configured yet.</div>
        ) : (
          <div style={{ display: "grid", gap: 12 }}>
            {providers.map((provider) => (
              <div
                key={provider.id}
                style={{
                  border: "1px solid #e2e8f0",
                  borderRadius: 14,
                  padding: 16,
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  alignItems: "flex-start",
                  color: black,
                }}
              >
                <div style={{ display: "grid", gap: 4 }}>
                  <div style={{ fontWeight: 800, color: black }}>
                    {provider.provider_name} <span style={{ color: black, fontWeight: 600 }}>({provider.provider_code})</span>
                  </div>
                  <div style={{ fontSize: 13, color: black }}>{(provider.service_types || []).join(", ") || "No service types configured"}</div>
                  <div style={{ fontSize: 13, color: black }}>{provider.contact_person || "-"} · {provider.contact_phone || "-"} · {provider.contact_email || "-"}</div>
                  <div style={{ fontSize: 13, color: black }}>{provider.city || "-"}, {provider.state || "-"} {provider.pincode || ""}</div>
                </div>
                <button
                  type="button"
                  onClick={() => openEditModal(provider)}
                  style={{ border: "1px solid #cbd5e1", borderRadius: 12, background: "#ffffff", color: black, padding: "10px 14px", cursor: "pointer", fontWeight: 700 }}
                >
                  Edit
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <ProviderModal
        open={showModal}
        editingId={editingId}
        form={form}
        setField={setField}
        saving={saving}
        error={error}
        success={success}
        onClose={closeModal}
        onSubmit={submit}
      />
    </div>
  );
}
